/**
 * Settings page. Shows the authenticated user's account information and a
 * change-password form. Note: the backend currently exposes no profile/password
 * mutation endpoint (see the routes table in the architecture contract), so the
 * change-password form validates client-side and informs the user the feature
 * is not yet available rather than silently doing nothing.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@/components/Cards/Card';
import Input from '@/components/Forms/Input';
import Button from '@/components/Forms/Button';
import { useToast } from '@/components/Alerts/toastContext';
import { useAuthStore } from '@/store/authStore';
import { validatePassword } from '@/utils/validators';

/** Settings / account view. */
export default function Settings(): ReactElement {
  const toast = useToast();
  const navigate = useNavigate();

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{ current?: string; next?: string; confirm?: string }>({});

  const handleChangePassword = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const nextErrors: { current?: string; next?: string; confirm?: string } = {};
    if (!currentPassword) nextErrors.current = 'Vui lòng nhập mật khẩu hiện tại';
    const pwdError = validatePassword(newPassword);
    if (pwdError) nextErrors.next = pwdError;
    if (confirmPassword !== newPassword) nextErrors.confirm = 'Mật khẩu xác nhận không khớp';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    // No backend endpoint yet — be transparent instead of faking success.
    toast.info('Tính năng đổi mật khẩu sẽ sớm được hỗ trợ.');
  };

  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cài đặt</h1>
        <p className="mt-1 text-sm text-gray-500">Quản lý thông tin tài khoản và tùy chọn.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Account info */}
        <Card title="Thông tin tài khoản">
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">Email</dt>
              <dd className="font-medium text-gray-900">{user?.email ?? '—'}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">Mã người dùng</dt>
              <dd className="truncate font-mono text-xs text-gray-700" title={user?.id}>
                {user?.id ?? '—'}
              </dd>
            </div>
          </dl>
        </Card>

        {/* Change password */}
        <Card title="Đổi mật khẩu">
          <form onSubmit={handleChangePassword} noValidate className="space-y-4">
            <Input
              label="Mật khẩu hiện tại"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              error={errors.current}
            />
            <Input
              label="Mật khẩu mới"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={errors.next}
            />
            <Input
              label="Xác nhận mật khẩu mới"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={errors.confirm}
            />
            <Button type="submit">Cập nhật mật khẩu</Button>
          </form>
        </Card>
      </div>

      {/* Session */}
      <Card title="Phiên đăng nhập">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-600">Đăng xuất khỏi thiết bị này.</p>
          <Button variant="danger" onClick={handleLogout}>
            Đăng xuất
          </Button>
        </div>
      </Card>
    </div>
  );
}
